import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QuestionTooltip } from "./QuestionTooltip";
import { RepeatingGroupQuestion as RepeatingGroupQuestionType, RepeatingGroupFieldDef } from "./questionTypes";
import { Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface RepeatingGroupQuestionProps {
  question: RepeatingGroupQuestionType;
  value: string; // JSON string of entries array
  onChange: (questionId: string, value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
}

type Entry = Record<string, string | number>;

function emptyEntry(fields: RepeatingGroupFieldDef[]): Entry {
  const e: Entry = {};
  fields.forEach((f) => {
    e[f.key] = f.type === 'number' ? 0 : '';
  });
  return e;
}

export function RepeatingGroupQuestion({ question, value, onChange, onValidationChange }: RepeatingGroupQuestionProps) {
  const fields = question.fields ?? [];
  const minEntries = question.minEntries ?? 1;
  const maxEntries = question.maxEntries ?? 10;
  const addLabel = question.addLabel ?? "Add Another";
  const sumValidation = question.sumValidation;

  const [entries, setEntries] = useState<Entry[]>(() => {
    try {
      const parsed = JSON.parse(value || "[]");
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : [emptyEntry(fields)];
    } catch {
      return [emptyEntry(fields)];
    }
  });

  // Sync entries to parent
  useEffect(() => {
    onChange(question.id, JSON.stringify(entries));
  }, [entries]);

  // Compute sum validation
  const sumField = sumValidation?.field;
  const sumTarget = sumValidation?.target ?? 0;
  const currentSum = sumField
    ? entries.reduce((acc, e) => acc + (Number(e[sumField]) || 0), 0)
    : 0;
  const sumMet = !sumValidation || currentSum === sumTarget;

  useEffect(() => {
    onValidationChange?.(sumMet);
  }, [sumMet]);

  const updateEntry = (index: number, key: string, val: string | number) => {
    setEntries((prev) => prev.map((e, i) => (i === index ? { ...e, [key]: val } : e)));
  };

  const addEntry = () => {
    if (entries.length < maxEntries) {
      setEntries((prev) => [...prev, emptyEntry(fields)]);
    }
  };

  const removeEntry = (index: number) => {
    if (entries.length > minEntries) {
      setEntries((prev) => prev.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="space-y-8 w-full max-w-4xl mr-auto py-4 min-h-[400px] text-left">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-3">
          {question.title}
          {question.tooltip && <QuestionTooltip tooltip={question.tooltip} />}
        </h2>
        {question.description && <p className="text-muted-foreground">{question.description}</p>}
      </div>

      <div className="space-y-4">
        {entries.map((entry, idx) => (
          <div key={idx} className="border border-border rounded-lg p-4 space-y-3 relative">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-muted-foreground">Entry {idx + 1}</span>
              {entries.length > minEntries && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeEntry(idx)}
                  className="text-destructive hover:text-destructive/80 h-8 w-8 p-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fields.map((field) => (
                <div key={field.key} className="space-y-1">
                  <Label className="text-sm">{field.label}{field.required && <span className="text-destructive ml-0.5">*</span>}</Label>
                  {field.type === 'select' && field.options ? (
                    <Select
                      value={String(entry[field.key] || "")}
                      onValueChange={(v) => updateEntry(idx, field.key, v)}
                    >
                      <SelectTrigger className="w-full"><SelectValue placeholder={`Select ${field.label}`} /></SelectTrigger>
                      <SelectContent>
                        {field.options.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={field.type === 'number' ? 'number' : 'text'}
                      value={String(entry[field.key] ?? "")}
                      onChange={(e) =>
                        updateEntry(idx, field.key, field.type === 'number' ? Number(e.target.value) || 0 : e.target.value)
                      }
                      placeholder={field.label}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {entries.length < maxEntries && (
          <Button type="button" variant="outline" size="sm" onClick={addEntry} className="gap-1.5">
            <Plus className="h-4 w-4" /> {addLabel}
          </Button>
        )}
      </div>

      {sumValidation && (
        <div className={cn(
          "p-3 rounded-md border text-sm",
          sumMet ? "border-primary/30 bg-primary/5 text-primary"
                 : "border-destructive/30 bg-destructive/5 text-destructive"
        )}>
          <div className="flex items-center justify-between">
            <span>{sumValidation.message}</span>
            <span className="font-semibold">
              {currentSum}{sumField?.includes('pct') || sumField?.includes('percent') ? '%' : ''} / {sumTarget}{sumField?.includes('pct') || sumField?.includes('percent') ? '%' : ''}
            </span>
          </div>
          <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={cn("h-full rounded-full transition-all", sumMet ? "bg-primary" : "bg-destructive")}
              style={{ width: `${Math.min((currentSum / sumTarget) * 100, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
